import React, { useMemo } from "react";

import { Box, Typography } from "@material-ui/core";
import { fade, makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) => ({
  header: {
    marginBottom: theme.spacing(1.5),
  },
  title: {
    fontWeight: 600,
  },
  timeline: {
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  item: {
    display: "grid",
    gridTemplateColumns: "132px minmax(0, 1fr)",
    gap: theme.spacing(2),
    padding: theme.spacing(1.5, 0.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
      gap: theme.spacing(0.5),
    },
  },
  event: {
    position: "relative",
    paddingLeft: theme.spacing(1.5),
    "&::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 6,
      width: 6,
      height: 6,
      borderRadius: "50%",
      backgroundColor: theme.palette.primary.main,
      boxShadow: `0 0 0 3px ${fade(theme.palette.primary.main, 0.12)}`,
    },
  },
  empty: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 120,
    padding: theme.spacing(2),
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: 8,
    color: theme.palette.text.secondary,
    textAlign: "center",
  },
}));

const getEvents = (deal) => {
  const events = (deal?.stageHistories || []).map((history) => ({
    key: `stage-${history.id}`,
    occurredAt: history.changedAt,
    title: `${history.fromStageName || "Inicio"} → ${history.toStageName}`,
    detail: [history.user?.name ? `por ${history.user.name}` : "", history.note]
      .filter(Boolean)
      .join(" · "),
  }));

  if (deal?.closedAt && ["won", "lost"].includes(deal.status)) {
    events.push({
      key: `status-${deal.status}-${deal.closedAt}`,
      occurredAt: deal.closedAt,
      title:
        deal.status === "won"
          ? "Negocio marcado como ganho"
          : "Negocio marcado como perdido",
      detail: deal.status === "lost" ? deal.lossReason || "" : "",
    });
  }

  if (deal?.createdAt) {
    events.push({
      key: `created-${deal.createdAt}`,
      occurredAt: deal.createdAt,
      title: "Negocio criado",
      detail: "",
    });
  }

  return events.sort(
    (first, second) =>
      new Date(second.occurredAt || 0).getTime() -
      new Date(first.occurredAt || 0).getTime(),
  );
};

const DealHistoryPanel = ({ deal, formatDateTime }) => {
  const classes = useStyles();
  const events = useMemo(() => getEvents(deal), [deal]);

  return (
    <Box>
      <Box className={classes.header}>
        <Typography variant="h6" className={classes.title}>
          Historico
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Mudancas de etapa e status, mais recentes primeiro.
        </Typography>
      </Box>

      {events.length > 0 ? (
        <Box className={classes.timeline}>
          {events.map((event) => (
            <Box key={event.key} className={classes.item}>
              <Typography variant="caption" color="textSecondary">
                {formatDateTime(event.occurredAt)}
              </Typography>
              <Box className={classes.event}>
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                  {event.title}
                </Typography>
                {event.detail ? (
                  <Typography variant="caption" color="textSecondary">
                    {event.detail}
                  </Typography>
                ) : null}
              </Box>
            </Box>
          ))}
        </Box>
      ) : (
        <Box className={classes.empty}>
          <Typography variant="body2">
            Nenhum evento registrado para este negocio.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default DealHistoryPanel;
